import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useGetSearchResultsForMultipleQuery } from '../../graphql/search.generated';
import { EntityType, GlossaryNode, GlossaryTerm } from '../../types.generated';
import { IconStyleType } from '../entity/Entity';
import { ANTD_GRAY } from '../entity/shared/constants';
import { SearchBar } from '../search/SearchBar';
import ClickOutside from '../shared/ClickOutside';
import { useEntityRegistry } from '../useEntityRegistry';
import { AutoTooltip } from '../entity/shared/components/common/AutoTooltip';
import { useTranslation } from '../../i18n/useTranslation';

const GlossarySearchWrapper = styled.div`
    position: relative;
`;

const ResultsWrapper = styled.div`
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%);
    max-height: 380px;
    overflow: auto;
    padding: 8px;
    position: absolute;
    max-height: 210px;
    overflow: auto;
    width: calc(100% - 24px);
    left: 12px;
    top: 45px;
`;

const SearchResult = styled(Link)`
    color: #262626;
    display: block;
    height: 100%;
    overflow: hidden;
    padding: 6px 8px;
    width: 100%;
    &:hover {
        background-color: ${ANTD_GRAY[3]};
        color: #262626;
    }
`;

const IconWrapper = styled.span`
    margin-right: 8px;
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
`;

const PathText = styled.div`
    font-size: 14px;
    color: #8c8c8c;
    margin-left: 20px;
    margin-top: 2px;
`;

function GlossarySearch() {
    const [query, setQuery] = useState('');
    const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
    const entityRegistry = useEntityRegistry();
    const { t } = useTranslation();
    const { data } = useGetSearchResultsForMultipleQuery({
        variables: {
            input: {
                types: [EntityType.GlossaryTerm, EntityType.GlossaryNode],
                query,
                start: 0,
                count: 50,
            },
        },
        skip: !query,
    });

    const searchResults = data?.searchAcrossEntities?.searchResults;

    // Helper function to get the parent path for a glossary entity
    const getParentPath = (entity: GlossaryTerm | GlossaryNode): string | null => {
        const parentNodes = entity.parentNodes?.nodes;
        if (!parentNodes || parentNodes.length === 0) {
            return null;
        }

        // Build path from root to immediate parent (reversed order)
        const pathParts = [...parentNodes].reverse().map((node) => {
            return entityRegistry.getDisplayName(node.type, node);
        });

        return `${t('Glossary')} / ${pathParts.join(' / ')}`;
    };

    return (
        <GlossarySearchWrapper>
            <ClickOutside onClickOutside={() => setIsSearchBarFocused(false)}>
                <SearchBar
                    initialQuery={query || ''}
                    placeholderText={t('Search Glossary')}
                    suggestions={[]}
                    hideRecommendations
                    style={{
                        padding: 8,
                        borderBottom: '1px solid #EDF0F0',
                    }}
                    inputStyle={{
                        height: 30,
                        fontSize: 14,
                    }}
                    onSearch={() => null}
                    onQueryChange={(q) => setQuery(q)}
                    entityRegistry={entityRegistry}
                    onFocus={() => setIsSearchBarFocused(true)}
                />
                {isSearchBarFocused && searchResults && !!searchResults.length && (
                    <ResultsWrapper>
                        {searchResults.map((result) => {
                            const entity = result.entity as GlossaryTerm | GlossaryNode;
                            const parentPath = getParentPath(entity);

                            return (
                                <SearchResult
                                    key={result.entity.urn}
                                    to={`${entityRegistry.getEntityUrl(result.entity.type, result.entity.urn)}`}
                                    onClick={() => setIsSearchBarFocused(false)}
                                >
                                    <NameRow>
                                        <IconWrapper>
                                            {entityRegistry.getIcon(result.entity.type, 12, IconStyleType.ACCENT)}
                                        </IconWrapper>
                                        <AutoTooltip>
                                            {entityRegistry.getDisplayName(result.entity.type, result.entity)}
                                        </AutoTooltip>
                                    </NameRow>
                                    {parentPath && <PathText>{parentPath}</PathText>}
                                </SearchResult>
                            );
                        })}
                    </ResultsWrapper>
                )}
            </ClickOutside>
        </GlossarySearchWrapper>
    );
}

export default GlossarySearch;
